import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import ReactTooltip from 'react-tooltip';
import { useBulkCheckboxes } from '../reducers/use-bulk-checkboxes';
import type { ComponentProps } from '../types/data-table';
import type { Event } from '../types/event';
import DTCoreButtons from './data_table/dt-core-buttons';
import DTDateRange from './data_table/dt-date-range';
import DTForm from './data_table/dt-form';
import DTHeaderRow from './data_table/dt-header-row';
import DTRow from './data_table/dt-row';

const HIDE_CORE_BUTTONS = ['Event', 'Tracking'];

const DataTable = ({
  actions,
  dataRange,
  fields,
  handleCreate,
  name,
  options,
  rows = [],
  setOptions,
  setTableFilters,
  tableFilters,
  validation,
}: ComponentProps) => {
  const initIds = useRef<string[]>(rows.map((row) => row.id) || []);
  const [checked, setChecked] = useBulkCheckboxes(initIds.current);

  useEffect(() => {
    if (
      !_.isEqual(
        initIds.current,
        rows.map((row) => row.id),
      )
    ) {
      initIds.current = rows.map((row) => row.id);
      setChecked({ key: 'reset', type: 'RESET_CHECKS', ids: initIds.current });
    }
  }, [rows]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <div className='px-2'>
      <div className='pt-2 bg-white shadow rounded'>
        <div className='px-2 border-b'>
          <div className='flex flex-row mb-4'>
            <div className={HIDE_CORE_BUTTONS.includes(name) ? 'hidden' : 'ml-2'}>
              {actions ? (
                <DTCoreButtons
                  actions={actions}
                  fields={fields}
                  rowCount={rows.length}
                  tableName={name}
                />
              ) : null}
            </div>
            <div className={HIDE_CORE_BUTTONS.includes(name) ? 'hidden' : 'ml-2'}>
              <DTDateRange tableFilters={tableFilters} setTableFilters={setTableFilters} />
            </div>
            <div className='ml-2'>
              {handleCreate ? (
                <DTForm
                  fields={fields}
                  handleCreate={handleCreate}
                  modelName={name}
                  validation={validation}
                  events={rows as Event[]}
                />
              ) : null}
            </div>
            {actions ? (
              <span className='relative inline-flex'>
                {actions.networkStatus < 7 && (
                  <AiOutlineLoading3Quarters className='animate-spin ml-2 text-blue-900' />
                )}
              </span>
            ) : null}
          </div>
        </div>
        <div>
          <table className='table-auto w-full'>
            <DTHeaderRow
              actions={actions}
              dataRange={dataRange}
              checked={checked}
              fields={fields}
              options={options}
              setChecked={setChecked}
              setOptions={setOptions}
              setTableFilters={setTableFilters}
              tableFilters={tableFilters}
              tableName={name}
            />
            <tbody>
              {rows.map((row, idx) => {
                return (
                  <DTRow
                    actions={actions}
                    checked={checked}
                    fields={fields}
                    key={`row-${idx}`}
                    idx={idx}
                    row={row}
                    setChecked={setChecked}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
